import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api/api";
import { useCart } from "../context/CartContext";
import { CheckCircle, ArrowLeft, Upload, Copy } from "lucide-react";
import "./Checkout.css";

export default function Checkout() {
  const navigate = useNavigate();
  const { clearCart, fetchCartCount } = useCart();

  const [screenshot, setScreenshot] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const cartId = localStorage.getItem("cart_id");
  const token = localStorage.getItem("access");

  useEffect(() => {
    return () => previewUrl && URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setMessage("Please select an image file.");
      return;
    }

    setScreenshot(file);
    setPreviewUrl(URL.createObjectURL(file));
    setMessage("");
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setMessage(`${label} copied to clipboard`);
    setTimeout(() => setMessage(""), 2500);
  };

  const submitOrder = async () => {
    if (!cartId) {
      setMessage("Cart expired.");
      return;
    }
    if (!screenshot) {
      setMessage("Please upload payment screenshot.");
      return;
    }

    const formData = new FormData();
    formData.append("cart_id", cartId);
    formData.append("payment_screenshot", screenshot);

    try {
      setLoading(true);

      await API.post("/orders/create/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      await clearCart();
      await fetchCartCount();

      setShowModal(true);
      setScreenshot(null);
      setPreviewUrl(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && detail.includes("items available in stock")) {
        setMessage(detail);
      } else {
        setMessage("Order failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="checkout-page">
        <div className="checkout-card">
          <h1 className="checkout-title">Checkout</h1>
          <p className="checkout-subtitle">Please pay prepayement</p>

          {message && <div className="checkout-alert error">{message}</div>}

          <div className="form-grid">
            {/* Payment Accounts */}
            <div className="payment-methods">
              <h3>Payment Options</h3>

              <div className="payment-card">
                <div className="payment-info">
                  <strong>CBE</strong>
                  <span>1000732117548</span>
                </div>
                <button onClick={() => copyToClipboard("1000732117548", "CBE")}>
                  <Copy size={16} />
                </button>
              </div>

              <div className="payment-card">
                <div className="payment-info">
                  <strong>Telebirr</strong>
                  <span>0948577700</span>
                </div>
                <button onClick={() => copyToClipboard("0948577700", "Telebirr")}>
                  <Copy size={16} />
                </button>
              </div>
            </div>

            {/* Payment Screenshot */}
            <div className="form-group full-width">
              <label>Upload Payment Screenshot</label>
              <div className={`file-drop-zone ${screenshot ? "has-file" : ""}`}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  id="file"
                />
                <label htmlFor="file">
                  <Upload size={20} />
                  {screenshot ? "Screenshot selected" : "Upload screenshot"}
                </label>
              </div>

              {previewUrl && (
                <div className="screenshot-preview-container">
                  <img src={previewUrl} alt="Screenshot Preview" />
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              className={`checkout-btn-main ${loading ? "loading" : ""}`}
              onClick={submitOrder}
              disabled={loading}
            >
              {loading ? "Processing..." : "Submit Payment"}
            </button>

            {/* Back Button */}
            <button
              className="checkout-back-btn"
              onClick={() => navigate("/cart")}
            >
              <ArrowLeft size={16} />
              Return to Cart
            </button>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <CheckCircle size={60} />
            <h2>Order Successful</h2>
            <button className="modal-home-btn" onClick={() => navigate("/")}>
              Continue Shopping
            </button>
          </div>
        </div>
      )}
    </>
  );
}